/**
 * @module @yimoka/i18n/remote/fetch-bundle
 * @summary 远程拉取与归一化
 * @author keep
 * @since 2026-05-19
 */

import { isSuccess, type IHTTPResponse } from '@yimoka/shared';

import type { FetchRemoteBundle, FetchRemoteContext, RemoteSharedState } from '../types';

/** 拉取结果 */
export interface FetchBundleResult {
  /** 归一化后的 bundle，304 或空时为 undefined */
  bundle?: Record<string, unknown>;
  /** 是否请求失败（非 success 且非 304） */
  failed: boolean;
  /** 304 Not Modified */
  notModified: boolean;
}

/**
 * 拉取并归一化远程 bundle，更新共享状态。
 *
 * @param fetchRemote - app 注入
 * @param lang - 语种短码
 * @param ctx - 上下文
 * @param state - 共享远程状态
 * @returns 拉取结果
 */
export async function fetchRemoteBundle(
  fetchRemote: FetchRemoteBundle,
  lang: string,
  ctx: FetchRemoteContext,
  state: RemoteSharedState,
): Promise<FetchBundleResult> {
  if (state.inflight.has(lang)) {
    try {
      const pending = await state.inflight.get(lang)!;
      return {
        bundle: Object.keys(pending.bundle).length > 0 ? pending.bundle : undefined,
        failed: false,
        notModified: pending.notModified,
      };
    } catch {
      return { failed: true, notModified: false };
    }
  }

  const promise = (async (): Promise<{ bundle: Record<string, unknown>; notModified: boolean }> => {
    const response = await fetchRemote(lang, ctx);

    if (response.status === 304) {
      return { bundle: {}, notModified: true };
    }

    if (!isSuccess(response)) {
      state.failedLangs.add(lang);
      throw new Error(response.msg || '远程语言包请求失败');
    }

    state.failedLangs.delete(lang);
    // 业务侧 fetchRemote 必须返回 IHTTPResponse<NamespaceMap 表>，包内不再做归一化；
    // null / 非对象一律视为空响应，由调用侧走 bundled fallback
    const { data } = response;
    const bundle = data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {};
    state.memoryCache.set(lang, { data: bundle, timestamp: Date.now() });
    return { bundle, notModified: false };
  })();

  state.inflight.set(lang, promise);

  try {
    const { bundle, notModified } = await promise;
    return {
      bundle: Object.keys(bundle).length > 0 ? bundle : undefined,
      failed: false,
      notModified,
    };
  } catch {
    return { failed: true, notModified: false };
  } finally {
    state.inflight.delete(lang);
  }
}

/**
 * 读取内存缓存（TTL 内有效）。
 *
 * @param state - 共享状态
 * @param lang - 语种
 * @param ttlMs - TTL
 * @returns 缓存 bundle 或 undefined
 */
export function readMemoryCache(
  state: RemoteSharedState,
  lang: string,
  ttlMs: number,
): Record<string, unknown> | undefined {
  const item = state.memoryCache.get(lang);
  if (!item) {
    return undefined;
  }
  if (Date.now() - item.timestamp > ttlMs) {
    return undefined;
  }
  const { data } = item;
  if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return undefined;
}

/** 标记某语种远程加载失败 */
export function markRemoteFailed(state: RemoteSharedState, lang: string): void {
  state.failedLangs.add(lang);
}

/** 某语种是否已标记远程失败 */
export function isRemoteFailed(state: RemoteSharedState, lang: string): boolean {
  return state.failedLangs.has(lang);
}

/** 写入内存缓存 */
export function writeMemoryCache(
  state: RemoteSharedState,
  lang: string,
  data: Record<string, unknown>,
): void {
  state.memoryCache.set(lang, { data, timestamp: Date.now() });
}

/** 类型导出供测试 */
export type { IHTTPResponse };
